import Head from "next/head";
import React, { ReactNode, ReactElement, useState } from "react";
import { ErrorBoundary } from "components/ErrorBoundary/ErrorBoundary";
import Header from "../Header";
import FooterRedesign from "../FooterRedesign/FooterRedesign";
import ResponsiveGutter from "ui-kit/ResponsiveGutter";
import styles from "./Layout.module.css";
import {
  SiteNavigation_siteCollection_items_navigationCollection_items,
} from "../../../__generated__/graphql";
import ConsentAndTagManager from "./ConsentAndTagManager";
import { ErrorComponentFullPage } from "./ErrorPage";
import { i18n, useWebTranslation } from "translations";
import { getResolvedDomainByOrg, Org } from "utils/site";
import { BannerV2 } from "components/Banner/BannerV2";
import { useCanonicalUrl } from "hooks/useCanonicalUrl";
import { inProduction } from "../../../config";

interface LayoutProps {
  children: ReactNode;
  title?: string;
  description?: string;
  slim?: boolean;
  background?: string;
  noHeader?: boolean;
  noRobot?: boolean;
  // Error pages are served for URLs that have no page of their own, so they
  // have no URL to declare as canonical.
  noCanonical?: boolean;
  seoTitle?: string;
  seoKeywords?: string[];
  seoNoIndex?: boolean;
  seoNoFollow?: boolean;
  seo?: {
    hrefLang: Array<{ locale: string; uri: string; org: Org }> | null;
  };
  navigationItems?:
    | (SiteNavigation_siteCollection_items_navigationCollection_items | null)[]
    | null
    | undefined;
  siteLanguages?: (string | null)[] | null | undefined;
  publishedAt?: string;
  modifiedAt?: string;
  // Open Graph page type. Article pages pass "article"; everything else is a
  // website. Kept here because next/head does not dedupe property-keyed meta
  // tags, so a page-level tag would render alongside this one.
  ogType?: string;
  banner?: {
    text?: string | null;
    linkText?: string | null;
    linkHref?: string | null;
  } | null;
  hasHero?: boolean;
  // Opt in to the redesign dark-blue default text color. Website pages set this
  // via LayoutPage; signup uses the base Layout without it.
  redesignTextColor?: boolean;
}

function Layout({
  children,
  title = "",
  description = "",
  slim = false,
  noRobot = false,
  noCanonical = false,
  seoKeywords,
  seoNoFollow,
  seoNoIndex,
  seoTitle,
  seo,
  publishedAt,
  modifiedAt,
  ogType = "website",
  redesignTextColor = false,
}: LayoutProps): ReactElement {
  const { t } = useWebTranslation();

  const canonicalUrl = useCanonicalUrl();

  // Non-production deploys are also blocked via robots.txt, but that only
  // stops crawling — a leaked deep link could still be indexed without this.
  const robots = inProduction
    ? [
        seoNoIndex === true ? "noindex" : undefined,
        seoNoFollow === true ? "nofollow" : undefined,
      ].filter((x) => x !== undefined)
    : ["noindex", "nofollow"];

  return (
    <div className={redesignTextColor ? styles.redesignText : undefined}>
      <Head>
        <title>{seoTitle ?? title}</title>
        <link
          rel="apple-touch-icon"
          sizes="180x180"
          href="/apple-touch-icon.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="32x32"
          href="/favicon-32x32.png"
        />
        <link
          rel="icon"
          type="image/png"
          sizes="16x16"
          href="/favicon-16x16.png"
        />
        <link rel="manifest" href="/site.webmanifest" />
        {seo?.hrefLang?.map(({ locale, uri, org }) => (
          <link
            key={locale}
            rel="alternate"
            hrefLang={locale}
            href={new URL(
              uri,
              `https://${getResolvedDomainByOrg(org)}`
            ).toString()}
          />
        ))}
        {/*
          The per-locale alternates above are all country-restricted (en-DE,
          en-SE, en-FR). Without an x-default, English searchers outside those
          countries match no page. Emit x-default mirroring this page's primary
          English alternate as the fallback for unmatched locales/regions.

          Fallback only: the backend pageSlugsBySlug resolver emits its own
          x-default entry (resolved to the UK equivalent per page). Skip this
          block when the backend already provided one so we never render two
          x-default tags.
        */}
        {(() => {
          const hasBackendXDefault = seo?.hrefLang?.some(
            ({ locale }) => locale === "x-default"
          );
          const englishAlternate = seo?.hrefLang?.find(({ locale }) =>
            locale.startsWith("en")
          );
          return !hasBackendXDefault && englishAlternate ? (
            <link
              rel="alternate"
              hrefLang="x-default"
              href={new URL(
                englishAlternate.uri,
                `https://${getResolvedDomainByOrg(englishAlternate.org)}`
              ).toString()}
            />
          ) : null;
        })()}
        {!noCanonical && <link rel="canonical" href={canonicalUrl} />}
        {noRobot && <meta name="robots" content="none" />}
        {!!description && (
          <>
            <meta name="description" content={description} />
            <meta property="og:description" content={description} />
          </>
        )}
        <meta name="author" content="Lassie" />
        <meta property="og:title" content={title ?? seoTitle ?? "Lassie"} />
        <meta
          property="og:image"
          content="https://www.lassie.co/images/hero-image-og.png"
        />
        <meta
          name="image"
          content="https://www.lassie.co/images/hero-image-og.png"
        />
        {publishedAt && (
          <meta property="article:published_time" content={publishedAt} />
        )}
        {modifiedAt && (
          <meta property="article:modified_time" content={modifiedAt} />
        )}
        <meta property="og:type" content={ogType} />
        {i18n?.language && (
          <meta property="og:locale" content={i18n?.language} />
        )}
        <meta property="fb:app_id" content="723156671567662" />
        {seoKeywords && seoKeywords.length > 0 && (
          <meta name="keywords" content={seoKeywords.join(",")} />
        )}
        {robots.length > 0 && (
          <meta name="robots" content={robots.join(", ")} />
        )}
      </Head>
      <ConsentAndTagManager includeHotJar={!slim} />
      <ErrorBoundary
        fallback={
          <ErrorComponentFullPage
            title={t("500.title")}
            description={t("500.description")}
            imgSrc="/images/error-500.svg"
          />
        }
      >
        {children}
      </ErrorBoundary>
    </div>
  );
}

function LayoutPage({
  children,
  title = "",
  description = "",
  slim = false,
  noHeader,
  background,
  noRobot,
  noCanonical,
  seoTitle,
  seoNoIndex,
  seoNoFollow,
  seoKeywords,
  seo,
  siteLanguages,
  navigationItems,
  publishedAt,
  modifiedAt,
  ogType,
  banner,
  hasHero = false,
}: LayoutProps): ReactElement {
  const { t } = useWebTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  return (
    <Layout
      siteLanguages={siteLanguages}
      navigationItems={navigationItems}
      noRobot={noRobot}
      noCanonical={noCanonical}
      title={title}
      description={description}
      slim={slim}
      seoKeywords={seoKeywords}
      seoNoIndex={seoNoIndex}
      seoTitle={seoTitle}
      seoNoFollow={seoNoFollow}
      seo={seo}
      publishedAt={publishedAt}
      modifiedAt={modifiedAt}
      ogType={ogType}
      redesignTextColor
    >
      {banner?.text && banner?.linkText && banner?.linkHref && !isMenuOpen && (
        <BannerV2
          text={banner.text}
          linkText={banner.linkText}
          linkHref={banner.linkHref}
        />
      )}
      {!noHeader && (
        <Header
          siteLanguages={siteLanguages}
          navigationItems={navigationItems ?? []}
          onMenuOpenChange={setIsMenuOpen}
          hasHero={hasHero}
        />
      )}
      <main
        className={styles.container}
        style={background ? { background } : {}}
      >
        <ErrorBoundary
          fallback={
            <ErrorComponentFullPage
              title={t("500.title")}
              description={t("500.description")}
              imgSrc="/images/error-500.svg"
            />
          }
        >
          {children}
        </ErrorBoundary>
      </main>
      {/* Large spacer above the footer (matches the between-section gutter);
          replaces the footer's own top margin */}
      <ResponsiveGutter mobile={17} tablet={18} desktop={19} />
      <FooterRedesign />
    </Layout>
  );
}

export { Layout, LayoutPage };
